import type {
  GetOrganizationSecurityFocusRequest,
  GetOrganizationSpidrRequest,
  GetOrganizationSpidrAttachmentLinkRequest,
  GetOrganizationSecurityFocusEventsRequest,
  GetOrganizationSpidrSummaryRequest,
} from '@rtkwlf/track-public';
import { queryOptions, useMutation } from '@tanstack/react-query';

import { arrayToKeyedObject } from '@packages/utils/array-to-keyed-object/array-to-keyed-object';

import { useTrackApi } from './api-provider';
import { queryKeys } from './query-keys';

/**
 * queryOptions for getOrganizationSecurityFocus
 *
 * /api/v1/organizations/{orgID}/security-focuses/{focusID}
 */
export const useGetOrganizationSecurityFocusOptions = (
  params: Omit<GetOrganizationSecurityFocusRequest, 'orgID'>,
) => {
  const { client, pod, orgID } = useTrackApi();

  return queryOptions({
    queryKey: queryKeys.organizations.id.focuses.id.index(pod, {
      ...params,
      orgID,
    }),
    queryFn: () =>
      client.getOrganizationSecurityFocus({
        ...params,
        orgID,
      }),
  });
};

/**
 * queryOptions for getOrganizationSecurityFocus
 *
 * /api/v1/organizations/{orgID}/security-focuses
 */
export const useGetOrganizationSecurityFocusesOptions = () => {
  const { client, pod, orgID } = useTrackApi();

  return queryOptions({
    queryKey: queryKeys.organizations.id.focuses.index(pod, {
      orgID,
    }),
    queryFn: () =>
      client.getOrganizationSecurityFocuses({
        orgID,
      }),
  });
};

/**
 * Mutation for getting the attachment from a spidr
 */
export const useGetOrganizationSpidrAttachmentLinkMutation = () => {
  const { orgID, client } = useTrackApi();

  return useMutation({
    mutationFn: (
      params: Omit<GetOrganizationSpidrAttachmentLinkRequest, 'orgID'>,
    ) =>
      client.getOrganizationSpidrAttachmentLink.bind(client)({
        ...params,
        orgID,
      }),
  });
};

/**
 * queryOptions for getOrganizationSpidr
 *
 * /api/v1/organizations/{orgID}/spidrs/{spidrID}
 */
export const useGetOrganizationSpidrOptions = (
  params: Omit<GetOrganizationSpidrRequest, 'orgID'>,
) => {
  const { client, pod, orgID } = useTrackApi();

  return queryOptions({
    queryKey: queryKeys.organizations.id.spidrs.id.index(pod, {
      ...params,
      orgID,
    }),
    queryFn: () =>
      client.getOrganizationSpidr({
        ...params,
        orgID,
      }),
  });
};

/**
 * queryOptions for getOrganizationSecurityFocusEvents
 *
 * /api/v1/organizations/{orgID}/security-focuses/{focusID}/events
 */
export const useGetOrganizationSecurityFocusEventsOptions = (
  params: Omit<GetOrganizationSecurityFocusEventsRequest, 'orgID'>,
) => {
  const { client, pod, orgID } = useTrackApi();
  return queryOptions({
    queryKey: queryKeys.organizations.id.focuses.id.events(pod, {
      ...params,
      orgID,
    }),
    queryFn: () =>
      client.getOrganizationSecurityFocusEvents({
        ...params,
        orgID,
      }),
  });
};

/**
 * queryOptions for getOrganizationSpidr
 *
 * /api/v1/organizations/{orgID}/spidrs/{spidrID}/summary
 */
export const useGetOrganizationSpidrSummaryOptions = (
  params: Omit<GetOrganizationSpidrSummaryRequest, 'orgID'>,
) => {
  const { client, pod, orgID } = useTrackApi();

  return queryOptions({
    queryKey: queryKeys.organizations.id.spidrs.id.summary(pod, {
      ...params,
      orgID,
    }),
    queryFn: () =>
      client.getOrganizationSpidrSummary({
        ...params,
        orgID,
      }),
  });
};

/**
 * queryOptions for getOrganization
 *
 * /api/v1/organizations/{orgID}
 */
export const useGetOrganizationOptions = () => {
  const { client, pod, orgID } = useTrackApi();
  return queryOptions({
    queryKey: queryKeys.organizations.id.index(pod, {
      orgID,
    }),
    queryFn: () =>
      client.getOrganization({
        orgID,
      }),
    /**
     * 10 minutes
     */
    staleTime: 60 * 10,
  });
};

/**
 * queryOptions for getOrganizationSpidrBlueprints
 *
 * /api/v1/organizations/{orgID}/spidr-blueprints
 */
export const useGetOrganizationSpidrBlueprintsOptions = () => {
  const { client, pod, orgID } = useTrackApi();
  const params = {
    orgID,
  };
  return queryOptions({
    queryKey: queryKeys.organizations.id.spidrblueprints.index(pod, params),
    queryFn: async () => {
      const raw = await client.getOrganizationSpidrBlueprints(params);
      const blueprints = arrayToKeyedObject(raw, 'id');
      return {
        raw,
        blueprints,
      };
    },
    /**
     * 10 minutes
     */
    staleTime: 60 * 10,
  });
};

/**
 * queryOptions for getSpidrFrameworkCategories
 *
 * /api/v1/spidr-framework-categories
 */
export const useGetSpidrFrameworkCategoriesOptions = () => {
  const { client, pod } = useTrackApi();

  return queryOptions({
    queryKey: queryKeys.filters.spidrs(pod),
    queryFn: () => client.getSpidrFrameworkCategories(),
  });
};
